import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import bnTranslation from '../locales/bn.json';
import enTranslation from '../locales/en.json';

// Initialize i18next
i18n
  .use(initReactI18next)
  .init({
    resources: {
      bn: { translation: bnTranslation },
      en: { translation: enTranslation },
    },
    lng: 'en', // Default language
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false, // React already safes from XSS
    },
  });

type LanguageContextType = {
  language: string;
  changeLanguage: (lang: string) => void;
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState('en');
  const location = useLocation();
  const navigate = useNavigate();

  // Load language based on URL path or saved preference
  useEffect(() => {
    // Check if the URL path is "/bn"
    if (location.pathname === '/bn') {
      setLanguage('bn');
      i18n.changeLanguage('bn');
      localStorage.setItem('language', 'bn');
      
      // Redirect to root URL
      navigate('/', { replace: true });
    } else {
      // Otherwise load from localStorage as before
      const savedLanguage = localStorage.getItem('language');
      if (savedLanguage) {
        setLanguage(savedLanguage);
        i18n.changeLanguage(savedLanguage);
      }
    }
  }, [location.pathname, navigate]);

  const changeLanguage = (lang: string) => {
    setLanguage(lang);
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  
  return context;
}; 